/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useEffect } from 'react';
import { useNotifications } from '../context/NotificationContext';
import { Notification, NotificationType } from '../types/notifications';
import { X, Info, CheckCircle, AlertTriangle, Zap, ArrowRight, Brain } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';

const getIcon = (type: NotificationType) => {
    switch (type) {
        case 'success': return <CheckCircle className="text-green-500" size={20} />;
        case 'warning': return <AlertTriangle className="text-yellow-500" size={20} />;
        case 'error': return <AlertTriangle className="text-red-500" size={20} />;
        case 'signal': return <Brain className="text-nobel-gold" size={20} />;
        default: return <Info className="text-blue-500" size={20} />;
    }
};

const NotificationToast: React.FC<{ notification: Notification; onDismiss: (id: string) => void }> = ({ notification, onDismiss }) => {
    useEffect(() => {
        const timer = setTimeout(() => {
            onDismiss(notification.id);
        }, notification.duration || 6000);

        return () => clearTimeout(timer);
    }, [notification, onDismiss]);

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 50, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, x: 50, scale: 0.8, transition: { duration: 0.2 } }}
            className="w-full max-w-sm bg-white/80 backdrop-blur-md rounded-2xl shadow-soft-2xl border border-stone-200 overflow-hidden relative flex items-start p-4 gap-4"
        >
            <div className="shrink-0 pt-0.5">{getIcon(notification.type)}</div>
            <div className="flex-1">
                <h4 className="font-bold text-sm text-stone-900">{notification.title}</h4>
                <p className="text-xs text-stone-500 mt-1">{notification.message}</p>
                {notification.action && (
                    <button 
                        onClick={notification.action.onClick}
                        className="mt-3 text-xs font-bold text-nobel-gold hover:text-stone-900 transition-colors flex items-center gap-1"
                    >
                        {notification.action.label} <ArrowRight size={12} />
                    </button>
                )}
            </div>
            <button onClick={() => onDismiss(notification.id)} className="absolute top-2 right-2 p-1 text-stone-400 hover:text-stone-900 hover:bg-stone-100 rounded-full transition-colors">
                <X size={14} />
            </button>
        </motion.div>
    );
};

export const NotificationCenter: React.FC = () => {
    const { notifications, dismissNotification } = useNotifications();

    return (
        <div className="fixed top-24 right-4 z-[200] w-full max-w-sm space-y-3 pointer-events-auto">
            <AnimatePresence>
                {notifications.map(n => (
                    <NotificationToast key={n.id} notification={n} onDismiss={dismissNotification} />
                ))}
            </AnimatePresence>
        </div>
    );
};
